import { useEffect, useMemo, useRef, useState } from 'react'

import { IPaymentNotificationsUI } from './types'
import { IPaymentNotificationItem } from '@api/paymentNotifications'
import { useDeviceType } from '@hooks/useDeviceType'

import { ComponentsHeader } from '@primitive/sectionHeadings'
import { Collapse } from 'antd'

import imgEmty from '@img/empty.svg'
import logoYaSkill from '@img/logo_yaskill.svg'
import logoModSkill from '@img/logo_modskill.svg'
import { IconApplications, IconContracts, IconListeners, IconPayment } from '@img/icons'

import { Button, Empty, Flex, Loader, Pagination, Table, ToolTip } from '@ui/index'

import css from './ui.module.scss'


const NO_DATE_KEY = '__no_date__'
const PAST_PAYMENTS_KEY = '__past_payments__'
const NO_MONTH_KEY = '__no_month__'
const NO_WEEK_KEY = '__no_week__'

type PaymentNotificationDateGroup = {
    items: IPaymentNotificationItem[]
    month: number
    finalPerson: number
    finalCompany: number
}

type PaymentNotificationWeekGroup = PaymentNotificationDateGroup & {
    label: string
    order: number
}

type PaymentNotificationMonthGroup = PaymentNotificationDateGroup & {
    label: string
    order: number
}

const parseDate = (dateStr: string): Date | null => {
    if (!dateStr || dateStr.length !== 10) return null
    const [day, month, year] = dateStr.split('.')
    const date = new Date(Number(year), Number(month) - 1, Number(day))
    return isNaN(date.getTime()) ? null : date
}

const parseDisplayDate = (dateStr?: string | null): Date | null => {
    if (!dateStr) return null

    const trimmedDate = dateStr.trim()
    const crmDate = parseDate(trimmedDate)
    if (crmDate) return crmDate

    const sqlDate = trimmedDate.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!sqlDate) return null

    const date = new Date(Number(sqlDate[1]), Number(sqlDate[2]) - 1, Number(sqlDate[3]))
    return isNaN(date.getTime()) ? null : date
}

const formatDateHeader = (dateStr: string): string => {
    const date = parseDate(dateStr)
    if (!date) return 'Без даты'

    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
    const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
    return `${weekdays[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
}

const formatDateLong = (dateStr: string): string => {
    const date = parseDisplayDate(dateStr)
    if (!date) return 'Без даты'

    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
}

const formatDateShort = (date: Date): string => {
    const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
    return `${date.getDate()} ${months[date.getMonth()]}`
}

const getWeekStart = (date: Date): Date => {
    const weekStart = new Date(date)
    const dayNumber = weekStart.getDay() || 7
    weekStart.setHours(0, 0, 0, 0)
    weekStart.setDate(weekStart.getDate() - dayNumber + 1)
    return weekStart
}

const getWeekEnd = (weekStart: Date): Date => {
    const weekEnd = new Date(weekStart)
    weekEnd.setDate(weekEnd.getDate() + 6)
    return weekEnd
}

const getWeekKey = (dateStr: string): string => {
    const date = parseDate(dateStr)
    if (!date) return NO_WEEK_KEY
    const weekStart = getWeekStart(date)
    return `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`
}

const getMonthKey = (dateStr: string): string => {
    const date = parseDate(dateStr)
    if (!date) return NO_MONTH_KEY
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

const formatMonthHeader = (monthKey: string): string => {
    if (monthKey === NO_MONTH_KEY) return 'Без месяца платежа'
    const [year, month] = monthKey.split('-').map(Number)
    const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
    return `${months[month - 1] || 'Без месяца'} ${year}`
}

const formatWeekHeader = (weekKey: string): string => {
    if (weekKey === NO_WEEK_KEY) return 'Без даты платежа'
    const [year, month, day] = weekKey.split('-').map(Number)
    const weekStart = new Date(year, month - 1, day)
    const weekEnd = getWeekEnd(weekStart)
    return `ПН, ${formatDateShort(weekStart)} → ВС, ${formatDateShort(weekEnd)} ${weekEnd.getFullYear()}`
}

const getPaymentDateKey = (item: IPaymentNotificationItem): string => item.paymentDate || NO_DATE_KEY

const getPaymentTypeOrder = (item: IPaymentNotificationItem): number => {
    if (item.notificationType === 'person_month') return 0
    if (item.notificationType === 'person_final') return 1
    return 2
}

const getPaymentRowBackground = (item: IPaymentNotificationItem): string => {
    if (item.isArchive === 1) return '#303B44'
    return item.contractStatusColor || 'var(--background_nav_panel)'
}

const getShortContractTypeName = (item: IPaymentNotificationItem): string => item.contractTypeID === 2 ? 'ЮЛ' : 'ФЗ'

const getShortDocumentTypeName = (documentTypeName: string): string => {
    const normalized = documentTypeName.toLowerCase()
    if (normalized.includes('профессион')) return 'ПП'
    if (normalized.includes('повыш')) return 'ПК'
    if (normalized.includes('диплом')) return 'Диплом'
    return documentTypeName || '------'
}

const getShortAttestationTypeName = (attestationTypeName: string): string => {
    const normalized = attestationTypeName.toLowerCase()
    if (normalized.includes('итоговое тест')) return 'ИТ'
    if (normalized.includes('итоговая') || normalized.includes('работ')) return 'ВР'
    return attestationTypeName || '------'
}

const getPaymentNotificationDescription = (item: IPaymentNotificationItem): string => {
    if (item.notificationType === 'person_final') return 'Счёт за посл. мес.'
    if (item.notificationType === 'company_final') return 'Итоговый счёт'
    return item.notificationDescription
        .replace(/Сч[её]т за (\d+)-й месяц обучения/gi, 'Счёт за $1й месяц')
        .replace(/Сч[её]т за (\d+)-й месяц/gi, 'Счёт за $1й месяц')
        .replace(/-й/g, 'й')
        .replace(/\.+$/g, '')
}

const createDateGroup = (): PaymentNotificationDateGroup => ({ items: [], month: 0, finalPerson: 0, finalCompany: 0 })

const addItemToDateGroup = (group: PaymentNotificationDateGroup, item: IPaymentNotificationItem) => {
    group.items.push(item)
    if (item.notificationType === 'person_month') group.month++
    if (item.notificationType === 'person_final') group.finalPerson++
    if (item.notificationType === 'company_final') group.finalCompany++
}

const getPastWeekGroups = (items: IPaymentNotificationItem[]): [string, PaymentNotificationWeekGroup][] => {
    const groups = new Map<string, PaymentNotificationWeekGroup>()

    for (const item of items) {
        const weekKey = getWeekKey(item.paymentDate)
        const weekDate = weekKey === NO_WEEK_KEY ? null : parseDate(weekKey.split('-').reverse().join('.'))
        if (!groups.has(weekKey)) {
            groups.set(weekKey, {
                ...createDateGroup(),
                label: formatWeekHeader(weekKey),
                order: weekDate?.getTime() || Number.MAX_SAFE_INTEGER,
            })
        }

        addItemToDateGroup(groups.get(weekKey)!, item)
    }

    return Array.from(groups.entries()).sort(([, a], [, b]) => a.order - b.order)
}

const getPastMonthGroups = (items: IPaymentNotificationItem[]): [string, PaymentNotificationMonthGroup][] => {
    const groups = new Map<string, PaymentNotificationMonthGroup>()

    for (const item of items) {
        const monthKey = getMonthKey(item.paymentDate)
        const monthDate = monthKey === NO_MONTH_KEY ? null : new Date(Number(monthKey.split('-')[0]), Number(monthKey.split('-')[1]) - 1, 1)
        if (!groups.has(monthKey)) {
            groups.set(monthKey, {
                ...createDateGroup(),
                label: formatMonthHeader(monthKey),
                order: monthDate?.getTime() || Number.MAX_SAFE_INTEGER,
            })
        }

        addItemToDateGroup(groups.get(monthKey)!, item)
    }

    return Array.from(groups.entries()).sort(([, a], [, b]) => a.order - b.order)
}


/** Страница - Извещения о платежах */
export const PaymentNotificationsUI = ({
    isLoading, txtSearch, currentPageNumber, totalCount, pageSize, notificationsList,
    txtSearchOnChange, loadPrimaryData, handleOpenCardContract, handleOpenCardListener, handleOpenCardApplication,
}: IPaymentNotificationsUI) => {
    const formatter = new Intl.NumberFormat('ru-RU')
    const deviceType = useDeviceType()
    const actionIconSize = deviceType === 'mobile' ? '16px' : '20px'
    const actionButtonSize = deviceType === 'mobile' ? 'xs' : 'sm'

    /** Возвращает лого сайта */
    const returnLogo = (dbName: string) => {
        switch (dbName) {
            case 'modern_academy': return logoModSkill
            case 'yaskill': return logoYaSkill
            default: return logoModSkill
        }
    }

    const currentDateKey = useMemo(() => {
        const now = new Date()
        return `${String(now.getDate()).padStart(2, '0')}.${String(now.getMonth() + 1).padStart(2, '0')}.${now.getFullYear()}`
    }, [])

    /** Группировка извещений по дате предстоящего платежа. */
    const dateGroups = useMemo(() => {
        const groups = new Map<string, PaymentNotificationDateGroup>()
        const currentTime = parseDate(currentDateKey)?.getTime() || 0

        for (const item of notificationsList) {
            const dateKey = getPaymentDateKey(item)
            const paymentTime = parseDate(dateKey)?.getTime()
            const key = item.isArchive !== 1 && paymentTime !== undefined && paymentTime < currentTime ? PAST_PAYMENTS_KEY : dateKey
            if (!groups.has(key)) groups.set(key, createDateGroup())
            const group = groups.get(key)!
            addItemToDateGroup(group, item)
        }

        const getGroupArchiveRank = (key: string) => Math.min(...groups.get(key)!.items.map((item) => item.isArchive))
        const entries = Array.from(groups.entries())
        entries.sort(([a], [b]) => {
            const archiveDiff = getGroupArchiveRank(a) - getGroupArchiveRank(b)
            if (archiveDiff !== 0) return archiveDiff
            if (a === PAST_PAYMENTS_KEY) return -1
            if (b === PAST_PAYMENTS_KEY) return 1
            if (a === NO_DATE_KEY) return 1
            if (b === NO_DATE_KEY) return -1
            return (parseDate(a)?.getTime() || 0) - (parseDate(b)?.getTime() || 0)
        })

        entries.forEach(([, group]) => {
            group.items.sort((a, b) => {
                if (a.isArchive !== b.isArchive) return a.isArchive - b.isArchive
                if (a.paymentTimestamp !== b.paymentTimestamp) return a.paymentTimestamp - b.paymentTimestamp
                const typeDiff = getPaymentTypeOrder(a) - getPaymentTypeOrder(b)
                if (typeDiff !== 0) return typeDiff
                return a.contractNumber.localeCompare(b.contractNumber)
            })
        })

        return entries
    }, [notificationsList, currentDateKey])

    /** Ближайшая дата платежа: текущая или следующая после неё. */
    const highlightedDateKey = useMemo(() => {
        const datedKeys = dateGroups.map(([key]) => key).filter((key) => key !== NO_DATE_KEY && key !== PAST_PAYMENTS_KEY)
        if (datedKeys.includes(currentDateKey)) return currentDateKey

        const currentTime = parseDate(currentDateKey)?.getTime() || 0
        return datedKeys.find((key) => (parseDate(key)?.getTime() || 0) >= currentTime) || ''
    }, [dateGroups, currentDateKey])

    const defaultActiveKey = useMemo(() => {
        if (highlightedDateKey) return highlightedDateKey
        return dateGroups.some(([key]) => key === PAST_PAYMENTS_KEY) ? PAST_PAYMENTS_KEY : ''
    }, [dateGroups, highlightedDateKey])

    const allDateKeys = useMemo(() => dateGroups.map(([key]) => key), [dateGroups])
    const [activeKeys, setActiveKeys] = useState<string[]>([])
    const didInit = useRef(false)

    useEffect(() => {
        if (didInit.current) return
        if (dateGroups.length > 0) {
            didInit.current = true
            if (defaultActiveKey) setActiveKeys([defaultActiveKey])
        }
    }, [dateGroups, defaultActiveKey])

    /** При поиске раскрываем все даты, после очистки возвращаем ближайшую. */
    const prevSearch = useRef(txtSearch.value)
    useEffect(() => {
        if (!didInit.current) return

        const wasSearching = !!prevSearch.current
        const isSearching = !!txtSearch.value
        prevSearch.current = txtSearch.value

        if (isSearching) setActiveKeys(allDateKeys)
        else if (wasSearching && !isSearching) setActiveKeys(defaultActiveKey ? [defaultActiveKey] : [])
    }, [txtSearch.value, defaultActiveKey, allDateKeys])

    const renderPaymentNotificationRow = (item: IPaymentNotificationItem) => {
        const isCompany = item.contractTypeID === 2
        const firstListener = item.listenersList[0]
        const contactTitle = isCompany ? item.companyNameShort || item.companyContactFIO || 'ЮЛ' : firstListener?.fullName || 'ФЗ'
        const phoneNumber = firstListener?.phoneNumber || item.companyContactPhone || '- --- --- -- --'
        const attestationTypeName = firstListener?.attestationTypeName || '------'
        const debtText = item.debt > 0 ? `${formatter.format(item.debt)} ₽` : 'Оплачено'

        return (
            <Table.Row
                key={item.notificationID}
                className={css['table_grid_columns_content']}
                onClick={() => handleOpenCardContract(true, item.contractID)}
            >
                <img src={returnLogo(item.dataBase)} alt="Лого сайта" />

                <div className={css['record_badge']} style={{ background: getPaymentRowBackground(item) }}>
                    <p>{item.applicationID}</p>
                    <p>{item.contractNumber}</p>
                </div>

                <div className={css['record_primary']}>
                    <p>{contactTitle}</p>
                    <p className={css['record_secondary_text']}>{getShortContractTypeName(item)} / {item.contractViewName || '------'}</p>
                </div>

                <div className={`${css['record_primary']} ${css['hidden_690']}`}>
                    <p>{getShortDocumentTypeName(item.documentTypeName)} / {getShortAttestationTypeName(attestationTypeName)} / {formatDateLong(item.dateEducationEnd)}</p>
                    <p className={css['record_secondary_text']}>{phoneNumber}</p>
                </div>

                <div className={`${css['record_primary']} ${css['hidden_900']}`}>
                    <p>{item.notificationTypeName}{item.isArchive === 1 ? ' / Архив' : ''}</p>
                    <b className={css['record_secondary_text']}>{formatter.format(item.paymentSum)} ₽ 👉 {debtText}</b>
                </div>

                <div className={`${css['record_dates']} ${css['hidden_1000']}`}>
                    <p>{item.paymentDate || 'Без даты'}</p>
                    <p className={css['record_secondary_text']}>{getPaymentNotificationDescription(item)}</p>
                </div>

                <Flex gap="2px" flexDirection="row" alignItems="center" justifyContent="center" className={css['record_actions']}>
                    <ToolTip title='Анкета договора' placement="bottomRight">
                        <Button
                            variant="icon"
                            size={actionButtonSize}
                            onClick={(e) => {
                                handleOpenCardContract(true, item.contractID)
                                e.stopPropagation()
                            }}
                        >
                            <IconContracts width={actionIconSize} height={actionIconSize} />
                        </Button>
                    </ToolTip>

                    <ToolTip title={firstListener ? 'Анкета слушателя' : 'Слушатель не найден'} placement="bottomRight">
                        <Button
                            variant="icon"
                            size={actionButtonSize}
                            isDisabled={!firstListener}
                            onClick={(e) => {
                                if (firstListener) handleOpenCardListener(true, firstListener.listenerID)
                                e.stopPropagation()
                            }}
                        >
                            <IconListeners width={actionIconSize} height={actionIconSize} />
                        </Button>
                    </ToolTip>

                    <ToolTip title='Анкета заявки' placement="bottomRight">
                        <Button
                            variant="icon"
                            size={actionButtonSize}
                            onClick={(e) => {
                                handleOpenCardApplication(true, item.applicationID)
                                e.stopPropagation()
                            }}
                        >
                            <IconApplications width={actionIconSize} height={actionIconSize} />
                        </Button>
                    </ToolTip>
                </Flex>
            </Table.Row>
        )
    }

    const renderPastWeekGroups = (items: IPaymentNotificationItem[]) => {
        const monthGroups = getPastMonthGroups(items)

        return (
            <Collapse
                defaultActiveKey={[]}
                className={css['past_month_collapse']}
                expandIconPosition="end"
                size="small"
            >
                {monthGroups.map(([monthKey, monthGroup]) => (
                    <Collapse.Panel
                        key={monthKey}
                        header={
                            <span className={css['past_month_header']}>
                                <span>{monthGroup.label}</span>
                                <span className={css['past_month_count']}>{monthGroup.items.length}</span>
                            </span>
                        }
                    >
                        <Flex gap="8px" flexDirection="column" className={css['past_week_groups']}>
                            {getPastWeekGroups(monthGroup.items).map(([weekKey, weekGroup]) => (
                                <div key={weekKey} className={css['past_week_group']}>
                                    <div className={css['past_week_header']}>
                                        <span>{weekGroup.label}</span>
                                        <span className={css['past_week_count']}>{weekGroup.items.length}</span>
                                    </div>

                                    <Flex gap="0px" flexDirection="column">
                                        {weekGroup.items.map(renderPaymentNotificationRow)}
                                    </Flex>
                                </div>
                            ))}
                        </Flex>
                    </Collapse.Panel>
                ))}
            </Collapse>
        )
    }

    const footer = (
        <div className={css['footer']}>
            <Flex gap='4px' flexDirection="row">
                <ToolTip title='Общее количество извещений' placement="topLeft">
                    <p className={css['stat_all']}>{totalCount}</p>
                </ToolTip>
            </Flex>

            <Pagination totalCount={totalCount} pageSize={pageSize} currentPage={currentPageNumber.value} onChange={(e) => currentPageNumber.setValue(e)} />
        </div>
    )

    return <div className={css['container']}>

        <ComponentsHeader
            headerIcon={<IconPayment width="20px" height="20px" />}
            headerTitle="Извещения о платежах"
            txtSearch={txtSearch.value}
            txtSearchTooltip={<>Поиск по:<br />✓ Номеру договора<br />✓ Номеру заявки<br />✓ ФИО слушателя<br />✓ Телефону<br />✓ Email<br />✓ Организации<br />✓ Курсу<br />✓ Программе</>}
            txtSearchOnChange={(e) => txtSearchOnChange(e.target.value)}
            txtSearchClearOnClick={() => txtSearchOnChange('')}
            btnUpdateOnClick={() => loadPrimaryData(txtSearch.value)}
        />

        <Table.Container footer={footer} footerPlacement='page'>
            <Loader isOpen={isLoading} backdrop title="Загрузка извещений..." />
            <Table.Header paddingLeft="50px" paddingRight="18px" classNameContainer={css['hidden_720']} classNameContent={css['table_grid_columns_header']}>
                <div style={{ textAlign: 'center' }}>
                    <p>№ заявки</p>
                    <p>№ договора</p>
                </div>
                <p>ФИО клиента</p>
                <div className={css['hidden_690']}>
                    <p>Документ / ИТ / Конец</p>
                    <p>Телефон</p>
                </div>
                <p className={css['hidden_900']}>Извещение<br />Сумма / Долг</p>
                <p className={css['hidden_1000']} style={{ textAlign: 'center' }}>Дата платежа</p>
                <p style={{ textAlign: 'center' }}>Действия</p>
            </Table.Header>

            <Table.Content>
                {!notificationsList.length
                    ? <Empty image={imgEmty} title='Данные отсутствуют' />
                    : (
                        <Collapse
                            activeKey={activeKeys}
                            onChange={(keys) => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
                            className={css['date_collapse']}
                            expandIconPosition="end"
                            size="small"
                        >
                            {dateGroups.map(([dateKey, group]) => {
                                const isNoDate = dateKey === NO_DATE_KEY
                                const isPastPayments = dateKey === PAST_PAYMENTS_KEY
                                const isHighlighted = dateKey === highlightedDateKey

                                return (
                                    <Collapse.Panel
                                        key={dateKey}
                                        className={isHighlighted ? css['panel_closest'] : ''}
                                        header={
                                            <span className={css['collapse_header']}>
                                                <span className={`${css['collapse_date']} ${isHighlighted ? css['collapse_date_closest'] : ''}`}>{isPastPayments ? 'Прошедшие платежи' : isNoDate ? 'Без даты' : formatDateHeader(dateKey)}</span>
                                                <span className={css['collapse_counts']}>
                                                    {group.month > 0 && <span className={`${css['count_badge']} ${css['count_blue']}`}>{group.month}</span>}
                                                    {group.finalPerson > 0 && <span className={`${css['count_badge']} ${css['count_yellow']}`}>{group.finalPerson}</span>}
                                                    {group.finalCompany > 0 && <span className={`${css['count_badge']} ${css['count_entity']}`}>{group.finalCompany}</span>}
                                                </span>
                                            </span>
                                        }
                                    >
                                        {isPastPayments ? renderPastWeekGroups(group.items) : group.items.map(renderPaymentNotificationRow)}
                                    </Collapse.Panel>
                                )
                            })}
                        </Collapse>
                    )
                }
            </Table.Content>
        </Table.Container>
    </div>
}
